/**
 * CustomerModal — Modal para crear y editar clientes (VEN-01)
 */
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Loader2 } from 'lucide-react'
import type { Customer } from '@/modules/sales/hooks/useSales'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { useCostCenters } from '@/modules/accounting/hooks/useAccounting'
import { useLocalizationConfig } from '@/modules/config/hooks/useConfig'
import { paisUsaDetraccion } from '@/lib/documentNumbering'
import { Modal } from '@/components/ds/Modal'

interface CustomerModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: Partial<Customer>) => void
  isPending: boolean
  initial?: Partial<Customer> | null
  taxIdLabel?: string
  serverErrors?: Record<string, string>
  readOnly?: boolean
}

export function CustomerModal({ open, onClose, onSubmit, isPending, initial, taxIdLabel = 'RUT', serverErrors, readOnly = false }: CustomerModalProps) {
  const { register, handleSubmit, reset, setError, formState: { errors } } = useForm<Partial<Customer>>()
  const { data: ccData } = useCostCenters({ is_active: 'true' })
  const costCenters = ccData?.cost_centers ?? []
  // El tipo de documento del catálogo 06 es peruano: en Chile el RUT es único y
  // no hay tipos, así que el selector solo se muestra donde significa algo.
  const { data: locConfig } = useLocalizationConfig()
  const usaTipoDocumento = paisUsaDetraccion(locConfig?.country_code)

  useEffect(() => {
    if (serverErrors) {
      Object.entries(serverErrors).forEach(([field, message]) => {
        setError(field as keyof Customer, { type: 'server', message })
      })
    }
  }, [serverErrors, setError])

  useEffect(() => {
    if (open) reset(initial || {})
  }, [open, initial, reset])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  return (
    <Modal open onClose={onClose} title={readOnly ? 'Detalle del cliente' : initial?.id ? 'Editar cliente' : 'Nuevo cliente'} size="2xl">
      <form onSubmit={readOnly ? e => e.preventDefault() : handleSubmit(onSubmit)} className="space-y-3">
        <fieldset disabled={readOnly} className="contents">
        {/* Datos principales */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField
            dense
            className="sm:col-span-2"
            label="Razón Social"
            required
            placeholder="Nombre legal o nombre completo"
            inputProps={register('legal_name', { required: 'La Razón Social es obligatoria.' })}
            error={errors.legal_name?.message}
          />
          <TextField
            dense
            label="Nombre de Fantasía"
            placeholder="Nombre comercial (opcional)"
            inputProps={register('trade_name')}
          />
          <TextField
            dense
            label={taxIdLabel}
            required
            placeholder="76.354.771-9"
            inputProps={register('tax_id', { required: `El ${taxIdLabel} es obligatorio.` })}
            error={errors.tax_id?.message}
          />
          <SelectField dense label="Tipo de persona" selectProps={register('person_type')}>
            <option value="juridica">Persona Jurídica</option>
            <option value="natural">Persona Natural</option>
          </SelectField>
          {usaTipoDocumento && (
            <SelectField
              dense
              label="Tipo de documento de identidad"
              selectProps={register('tax_id_type')}
              hint="Para un cliente del exterior elegir «sin documento tributario»: su identificador no cumple el dígito verificador del RUC, y es el dato que exige una factura de exportación."
            >
              <option value="">Detectar automáticamente</option>
              <option value="6">RUC</option>
              <option value="1">DNI</option>
              <option value="0">Sin documento tributario (no domiciliado)</option>
            </SelectField>
          )}
          <TextField
            dense
            className="sm:col-span-2"
            label="Giro Comercial"
            placeholder="Comercio al por menor"
            inputProps={{ ...register('giro'), maxLength: 80 }}
          />
        </div>

        {/* Contacto */}
        <div>
          <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-3">Contacto</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              dense
              label="Email comercial"
              placeholder="contacto@empresa.cl"
              inputProps={{
                ...register('email_contact', {
                  pattern: { value: /^\S+@\S+$/i, message: 'Email inválido.' },
                }),
                type: 'email',
              }}
              error={errors.email_contact?.message}
            />
            <TextField
              dense
              label="Email DTE"
              placeholder="dte@empresa.cl"
              inputProps={{
                ...register('email_dte', {
                  pattern: { value: /^\S+@\S+$/i, message: 'Email inválido.' },
                }),
                type: 'email',
              }}
              hint="Para recibir documentos tributarios"
              error={errors.email_dte?.message}
            />
            <TextField
              dense
              label="Teléfono"
              placeholder="+56 9 1234 5678"
              inputProps={register('phone')}
            />
            <TextField
              dense
              label="Nombre de contacto"
              placeholder="Juan Pérez"
              inputProps={register('contact_name')}
            />
          </div>
        </div>

        {/* Dirección */}
        <div>
          <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-3">Dirección</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              dense
              className="sm:col-span-2"
              label="Dirección"
              placeholder="Av. Principal 1234, Of. 5"
              inputProps={register('address_street')}
            />
            <TextField
              dense
              label="Comuna"
              placeholder="Providencia"
              inputProps={{ ...register('address_commune'), maxLength: 60 }}
            />
            <TextField
              dense
              label="Ciudad"
              placeholder="Santiago"
              inputProps={register('address_city')}
            />
            <TextField
              dense
              label="Región"
              placeholder="Metropolitana"
              inputProps={register('address_region')}
            />
          </div>
        </div>

        {/* Condiciones comerciales */}
        <div>
          <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-3">Condiciones comerciales</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              dense
              label="Plazo de pago (días)"
              placeholder="0 = contado"
              inputProps={{
                ...register('payment_term_days', { valueAsNumber: true }),
                type: 'number', min: 0, defaultValue: 0,
              }}
            />
            <TextField
              dense
              label="Límite de crédito"
              placeholder="Sin límite"
              inputProps={{ ...register('credit_limit'), type: 'number', min: 0, step: 1 }}
            />
            <SelectField
              dense
              className="sm:col-span-2"
              label="Centro de costo por defecto"
              selectProps={register('default_cost_center')}
              hint="Último recurso cuando ni el producto ni su categoría definen centro. Sirve cuando el área que ejecuta depende del contrato y no de lo que se vende."
            >
              <option value="">— Sin centro por defecto —</option>
              {costCenters.map(cc => (
                <option key={cc.id} value={cc.id}>{cc.code} · {cc.name}</option>
              ))}
            </SelectField>
            <TextField
              dense
              className="sm:col-span-2"
              label="Notas internas"
              multiline
              rows={2}
              placeholder="Observaciones para uso interno..."
              inputProps={register('notes')}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-2 border-t border-divider">
          {readOnly ? (
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
              Cerrar
            </button>
          ) : (
            <>
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
                Cancelar
              </button>
              <button type="submit" disabled={isPending}
                className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors">
                {isPending && <Loader2 size={14} className="animate-spin" />}
                {initial?.id ? 'Guardar cambios' : 'Crear cliente'}
              </button>
            </>
          )}
        </div>
        </fieldset>
      </form>
    </Modal>
  )
}
